import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

gsap.registerPlugin(ScrollTrigger);

const {
  FiSettings,
  FiShoppingCart,
  FiTruck,
  FiActivity,
  FiDollarSign,
  FiBookOpen,
  FiCheckCircle,
  FiArrowRight,
  FiShield,
  FiCpu,
  FiBarChart2,
  FiSearch,
  FiTool,
  FiLayers,
  FiZap,
  FiUsers,
  FiGlobe,
  FiMessageCircle,
  FiCalendar,
  FiClipboard,
  FiServer,
  FiLock
} = FiIcons;

const metrics = [
  { value: "10+", label: "Custom systems delivered and actively used every day by operational teams since 2018", icon: FiLayers },
  { value: "1M+", label: "Records processed per month across live client databases in production today", icon: FiBarChart2 },
  { value: "200+", label: "Concurrent users supported daily in our largest multi-department deployment", icon: FiUsers },
  { value: "5+", label: "Industries served across India and the UK since our founding", icon: FiGlobe },
  { value: "AI", label: "Models in live production — forecasting demand, classifying documents, detecting defects in images, and extracting data from invoices", icon: FiCpu },
  { value: "99.9%", label: "Uptime maintained across hosted client infrastructure over the past 12 months", icon: FiShield }
];

const caseStudies = [
  {
    industry: "Manufacturing",
    client: "Mid-size production company, 80+ employees",
    problem: "Relied on spreadsheets and disconnected tools to manage inventory, production scheduling, and invoicing — leading to frequent stock-outs and delayed billing.",
    built: "A unified ERP system covering inventory tracking, production workflow management, role-based dashboards, and automated invoice generation.",
    outcomes: [
      "Reduced stock discrepancies by 70% within the first 3 months",
      "Cut average invoicing cycle from 5 days to same-day"
    ]
  },
  {
    industry: "Logistics & Distribution",
    client: "Regional logistics operator, 3 warehouse locations",
    problem: "No visibility across warehouses — manual order routing, paper-based dispatch, and no real-time stock levels for the sales team.",
    built: "A multi-location inventory and dispatch platform with real-time dashboards, barcode scanning integration, and automated reorder triggers.",
    outcomes: [
      "Operational staff saved 12+ hours per week on manual reconciliation",
      "Order fulfilment accuracy improved from 88% to 98%"
    ]
  },
  {
    industry: "Healthcare & Compliance",
    client: "Health-tech startup, seed-funded",
    problem: "Needed a secure, role-based portal to manage patient intake forms, appointment scheduling, and compliance-ready document storage — with no in-house dev team.",
    built: "A cloud-hosted portal on Azure with granular access controls, encrypted document storage, and automated appointment reminders.",
    outcomes: [
      "Went from idea to production-ready MVP in under 10 weeks",
      "Passed initial compliance review with zero critical findings"
    ]
  }
];

const clientQuotes = [
  {
    quote: "They understood our operations better than most internal teams we've worked with — the system they built is now the backbone of our daily workflow.",
    role: "Operations Director",
    industry: "Manufacturing"
  },
  {
    quote: "We went from a rough brief to a working prototype in three weeks. No hand-holding required — they just got on with it.",
    role: "Founder & CEO",
    industry: "Health-tech"
  },
  {
    quote: "The AI model they deployed catches defects our manual QA process missed entirely. It paid for itself in the first quarter.",
    role: "Head of Quality",
    industry: "Production & Assembly"
  },
  {
    quote: "What stood out was their honesty — they told us what not to build first, which saved us months of wasted effort.",
    role: "CTO",
    industry: "Retail & E-Commerce"
  }
];

const capabilityGroups = [
  {
    title: "Streamline Operations",
    icon: FiSettings,
    description: "Replace disconnected spreadsheets and manual handoffs with a single system your team actually uses every day.",
    items: [
      { name: "Custom ERP & Business Systems", detail: "Unify inventory, orders, production, and finance so nothing falls through the cracks" },
      { name: "Inventory & Warehouse Management", detail: "Real-time stock levels across locations with automated reorder triggers" },
      { name: "Billing & Invoicing Automation", detail: "Cut invoice cycles from days to hours and reduce payment collection delays" },
      { name: "Production & Shop-Floor Workflows", detail: "Digitise scheduling, tracking, and quality control from raw material to dispatch" }
    ]
  },
  {
    title: "Grow Revenue & Visibility",
    icon: FiBarChart2,
    description: "Give leadership and sales teams live data, better customer insights, and tools that remove friction from the revenue cycle.",
    items: [
      { name: "Sales & CRM Platforms", detail: "Centralise customer data, track pipeline activity, and automate follow-ups" },
      { name: "Data Analytics Dashboards", detail: "Turn raw operational data into visual insights your team checks every morning" },
      { name: "Role-Based Access Portals", detail: "Secure portals where clients, vendors, or staff see only what they need" },
      { name: "REST APIs & System Integrations", detail: "Connect your tools — accounting, CRM, warehouse — so data flows without re-entry" }
    ]
  },
  {
    title: "Deploy Intelligent Automation",
    icon: FiCpu,
    description: "Apply AI and machine learning where it actually saves time — not as a buzzword, but as a tool that replaces hours of manual work.",
    items: [
      { name: "Demand Forecasting & Prediction", detail: "ML models trained on your data to predict sales volumes, stock needs, or capacity — so you plan ahead instead of reacting" },
      { name: "Document Extraction & Classification", detail: "Automatically read invoices, forms, or receipts and route the data into your system — no manual entry" },
      { name: "Computer Vision & Defect Detection", detail: "Inspect products, detect anomalies on production lines, or verify packaging using camera feeds and trained image models" },
      { name: "Workflow Automation Pipelines", detail: "Chain AI steps together: ingest data, validate, classify, route, and alert — end-to-end without human bottlenecks" }
    ]
  },
  {
    title: "Scale on Cloud Infrastructure",
    icon: FiServer,
    description: "Build on Azure with architecture that handles growth, stays available, and keeps your data secure.",
    items: [
      { name: "Azure Cloud Applications", detail: "Deploy globally available, auto-scaling apps on Microsoft's enterprise cloud" },
      { name: "Database Design & Migration", detail: "Structured, performant databases — whether you're starting fresh or migrating from legacy systems" },
      { name: "CI/CD & DevOps Pipelines", detail: "Automated testing, build, and deployment so updates ship safely and frequently" }
    ]
  }
];

const whyChooseUs = [
  { title: "Senior-only team", detail: "Every person on your project has 5+ years of production experience. No juniors, no learning on your time." },
  { title: "Production focus from day one", detail: "We build to ship. Every decision — architecture, tooling, testing — is made with live deployment in mind." },
  { title: "Deep .NET & Azure expertise", detail: "Our core stack is battle-tested across dozens of enterprise projects. We know where the edge cases hide." },
  { title: "Real AI, not demos", detail: "Our ML models run in production environments processing real data daily — forecasting, classifying, detecting." },
  { title: "Small team, full ownership", detail: "You talk directly to the people writing the code. No account managers, no layers. Fast decisions, fewer misunderstandings." }
];

const processPhases = [
  { phase: "Discover", icon: FiSearch, description: "We review your current setup, understand your workflows, and identify the highest-impact problems to solve first. You walk away with a clear picture of what to build and why." },
  { phase: "Design", icon: FiClipboard, description: "We map out system architecture, data flows, and user interfaces before writing any code. You approve wireframes and scope before development begins." },
  { phase: "Build", icon: FiTool, description: "We develop in short cycles with regular check-ins. You see working software every 1–2 weeks, not just status updates." },
  { phase: "Deploy", icon: FiZap, description: "We handle staging, testing, and go-live. Your team gets training and documentation so they can use the system confidently from day one." },
  { phase: "Support", icon: FiShield, description: "After launch, we monitor performance, fix issues, and ship enhancements. You get a reliable partner, not a handoff and a wave goodbye." }
];

const techStack = [
  { category: "Backend", items: ".NET, C#, ASP.NET Core, Entity Framework, SQL Server, PostgreSQL" },
  { category: "Frontend", items: "React, Tailwind CSS, Vite, Framer Motion" },
  { category: "Cloud & DevOps", items: "Microsoft Azure, Azure DevOps, Docker, CI/CD Pipelines" },
  { category: "AI & ML", items: "PyTorch, TensorFlow, Scikit-learn, OpenCV, Hugging Face, ONNX" },
  { category: "Data", items: "ECharts, Power BI, REST APIs, Supabase, Redis" }
];

const industries = [
  { icon: FiSettings, title: "Manufacturing & Production" },
  { icon: FiShoppingCart, title: "Retail & E-Commerce" },
  { icon: FiTruck, title: "Logistics & Supply Chain" },
  { icon: FiActivity, title: "Healthcare & Pharma" },
  { icon: FiDollarSign, title: "Finance & Accounting" },
  { icon: FiBookOpen, title: "Education & EdTech" }
];

const Capabilities = () => {
  // ── Ref buckets for GSAP stagger targets ────────────────────────────────
  const heroRef          = useRef(null);
  const metricsRef       = useRef([]);
  const caseStudiesRef   = useRef([]);
  const quotesRef        = useRef([]);
  const capGroupsRef     = useRef([]);
  const industriesRef    = useRef([]);
  const whyRef           = useRef([]);
  const processRef       = useRef([]);
  const techRef          = useRef([]);
  const reliabilityRef   = useRef(null);
  const securityRef      = useRef(null);
  const ctaMidRef        = useRef(null);
  const ctaFinalRef      = useRef(null);

  const push = (bucket) => (el) => { if (el) bucket.current.push(el); };

  useEffect(() => {
    const ctx = gsap.context(() => {

      // Helper: single ScrollTrigger stagger batch ─────────────────────────
      const revealBatch = (elements, triggerEl, opts = {}) => {
        if (!elements.length) return;
        gsap.fromTo(
          elements,
          { autoAlpha: 0, y: opts.y ?? 32, scale: opts.scale ?? 1 },
          {
            autoAlpha: 1, y: 0, scale: 1,
            duration: opts.duration ?? 0.7,
            ease: opts.ease ?? 'power3.out',
            stagger: { amount: opts.amount ?? 0.4, from: opts.from ?? 'start' },
            scrollTrigger: {
              trigger: triggerEl ?? elements[0],
              start: 'top 84%',
              once: true,
            },
          }
        );
      };

      // 1. Hero ─────────────────────────────────────────────────────────────
      gsap.fromTo(
        heroRef.current,
        { autoAlpha: 0, y: 28 },
        { autoAlpha: 1, y: 0, duration: 0.9, ease: 'expo.out' }
      );

      // 2. Metrics grid ─────────────────────────────────────────────────────
      revealBatch(metricsRef.current,     null, { y: 24, scale: 0.97, amount: 0.35 });

      // 3. Case studies ─────────────────────────────────────────────────────
      revealBatch(caseStudiesRef.current, null, { y: 32, amount: 0.4 });

      // 4. Client quotes ────────────────────────────────────────────────────
      revealBatch(quotesRef.current,      null, { y: 20, amount: 0.3 });

      // 5. Capability groups ────────────────────────────────────────────────
      revealBatch(capGroupsRef.current,   null, { y: 36, duration: 0.8, amount: 0.45, ease: 'expo.out' });

      // 6. Industries ───────────────────────────────────────────────────────
      revealBatch(industriesRef.current,  null, { y: 16, scale: 0.93, amount: 0.35, from: 'center' });

      // 7. Why choose us ────────────────────────────────────────────────────
      revealBatch(whyRef.current,         null, { y: 20, amount: 0.35 });

      // 8. Process phases ───────────────────────────────────────────────────
      revealBatch(processRef.current,     null, { y: 20, amount: 0.4 });

      // 9. Tech stack ───────────────────────────────────────────────────────
      revealBatch(techRef.current,        null, { y: 14, amount: 0.25 });

      // 10. Reliability & Security panels ───────────────────────────────────
      if (reliabilityRef.current) {
        gsap.fromTo(reliabilityRef.current,
          { autoAlpha: 0, x: -24 },
          { autoAlpha: 1, x: 0, duration: 0.8, ease: 'power3.out',
            scrollTrigger: { trigger: reliabilityRef.current, start: 'top 85%', once: true } }
        );
      }
      if (securityRef.current) {
        gsap.fromTo(securityRef.current,
          { autoAlpha: 0, x: 24 },
          { autoAlpha: 1, x: 0, duration: 0.8, ease: 'power3.out', delay: 0.1,
            scrollTrigger: { trigger: securityRef.current, start: 'top 85%', once: true } }
        );
      }

      // 11. Mid-page CTA ────────────────────────────────────────────────────
      if (ctaMidRef.current) {
        gsap.fromTo(ctaMidRef.current,
          { autoAlpha: 0, y: 24 },
          { autoAlpha: 1, y: 0, duration: 0.8, ease: 'power3.out',
            scrollTrigger: { trigger: ctaMidRef.current, start: 'top 85%', once: true } }
        );
      }

      // 12. Final CTA ───────────────────────────────────────────────────────
      if (ctaFinalRef.current) {
        gsap.fromTo(ctaFinalRef.current,
          { autoAlpha: 0, scale: 0.96 },
          { autoAlpha: 1, scale: 1, duration: 0.9, ease: 'expo.out',
            scrollTrigger: { trigger: ctaFinalRef.current, start: 'top 85%', once: true } }
        );
      }
    });

    return () => ctx.revert();
  }, []);

  return (
    <div className="pt-32 pb-0 relative overflow-hidden bg-white">
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.15]"></div>

      {/* Hero — Buyer-Outcome Focused */}
      <section className="max-w-7xl mx-auto px-6 relative z-10 mb-16">
        <div ref={heroRef} style={{ opacity: 0 }} className="max-w-4xl">
          <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
            Systems We <span className="text-gradient-cyan">Build and Run</span> for Clients
          </h1>
          <p className="text-slate-600 text-lg md:text-xl leading-relaxed mb-4">
            If you're running a growing business and need custom software, intelligent automation, or a reliable cloud platform — here's what we can handle for you end-to-end.
          </p>
          <p className="text-slate-500 text-base leading-relaxed mb-10">
            We work with teams in India and the UK. Our clients are typically SMEs, mid-market operators, and non-technical founders who need a strong implementation partner — not a slide deck.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/book-call"
              className="px-8 py-4 bg-slate-900 text-white font-bold text-xs uppercase tracking-widest hover:bg-black transition-all flex items-center justify-center gap-2 group shadow-lg"
            >
              Book a 30-Minute Discovery Call
              <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/contact"
              className="px-8 py-4 border border-slate-200 text-slate-900 font-bold text-xs uppercase tracking-widest hover:border-slate-900 hover:bg-slate-50 transition-all flex items-center justify-center gap-2"
            >
              Request a System Review
            </Link>
          </div>
          <p className="text-slate-400 text-xs mt-4">We usually respond within 1 business day.</p>
        </div>
      </section>

      {/* Metrics — Proof Near Top */}
      <section className="py-20 bg-slate-50 border-y border-slate-100 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
            <span className="w-8 h-[2px] bg-cyan-500"></span>
            Real Systems in Production
          </h2>
          <p className="text-slate-500 text-sm mb-12 max-w-2xl">
            These are not projections. Every number below reflects work we've delivered and systems running in live environments today.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {metrics.map((metric, index) => (
              <div
                key={index}
                ref={push(metricsRef)}
                style={{ opacity: 0 }}
                className="bg-white border border-slate-200 p-6 flex items-start gap-5 hover:border-cyan-300 transition-all shadow-sm"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-cyan-50 flex items-center justify-center">
                  <SafeIcon icon={metric.icon} className="text-xl text-cyan-600" />
                </div>
                <div>
                  <div className="text-3xl font-black text-cyan-600 tabular-nums mb-1">{metric.value}</div>
                  <div className="text-slate-500 text-xs font-medium leading-relaxed">{metric.label}</div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-slate-500 text-sm mt-8 leading-relaxed max-w-3xl">
            When we say "enterprise systems", we mean multi-department platforms used daily by 20–200+ staff — covering operations, finance, warehouse, and management functions. When we say "AI models in production", we mean trained models running on live data: forecasting demand, classifying documents, extracting invoice data, and detecting product defects on camera feeds.
          </p>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-24 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
            <span className="w-8 h-[2px] bg-cyan-500"></span>
            What We've Delivered
          </h2>
          <p className="text-slate-500 text-sm mb-12 max-w-2xl">
            Anonymized summaries of real projects. We're happy to discuss specifics in a private conversation.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {caseStudies.map((cs, index) => (
              <div
                key={index}
                ref={push(caseStudiesRef)}
                style={{ opacity: 0 }}
                className="bg-white border border-slate-200 p-8 flex flex-col hover:border-cyan-300 transition-all shadow-sm"
              >
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-600 bg-cyan-50 px-3 py-1 border border-cyan-200 w-fit mb-5">
                  {cs.industry}
                </span>
                <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mb-4">{cs.client}</p>
                <p className="text-sm text-slate-600 mb-4 leading-relaxed"><span className="font-bold text-slate-800">Problem:</span> {cs.problem}</p>
                <p className="text-sm text-slate-600 mb-5 leading-relaxed"><span className="font-bold text-slate-800">What we built:</span> {cs.built}</p>
                <div className="mt-auto pt-4 border-t border-slate-100">
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-3">Outcomes</p>
                  {cs.outcomes.map((outcome, i) => (
                    <div key={i} className="flex items-start gap-2 mb-2">
                      <SafeIcon icon={FiCheckCircle} className="text-cyan-500 mt-0.5 flex-shrink-0 text-sm" />
                      <span className="text-sm text-slate-700 font-medium leading-relaxed">{outcome}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Quotes */}
      <section className="py-20 bg-slate-50 border-y border-slate-100 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-12 flex items-center gap-3">
            <span className="w-8 h-[2px] bg-cyan-500"></span>
            What Clients Say
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {clientQuotes.map((q, index) => (
              <div
                key={index}
                ref={push(quotesRef)}
                style={{ opacity: 0 }}
                className="bg-white border border-slate-200 p-8 shadow-sm hover:border-cyan-300 transition-all"
              >
                <SafeIcon icon={FiMessageCircle} className="text-cyan-400 text-lg mb-4" />
                <p className="text-slate-700 text-sm leading-relaxed mb-5 italic">"{q.quote}"</p>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                  {q.role} <span className="text-slate-300 mx-2">|</span> {q.industry}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mid-Page CTA */}
      <section className="py-16 relative z-10">
        <div ref={ctaMidRef} style={{ opacity: 0 }} className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-2xl font-bold text-slate-900 mb-4">Ready to see what this looks like for your business?</h3>
          <p className="text-slate-500 text-sm mb-8">Walk us through your challenge in a 30-minute call. We'll tell you what we'd build, how long it would take, and what it would cost.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/book-call"
              className="px-8 py-4 bg-slate-900 text-white font-bold text-xs uppercase tracking-widest hover:bg-black transition-all inline-flex items-center justify-center gap-2 group shadow-lg"
            >
              Book a Discovery Call
              <SafeIcon icon={FiCalendar} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/contact"
              className="px-8 py-4 border border-slate-200 text-slate-900 font-bold text-xs uppercase tracking-widest hover:border-slate-900 hover:bg-slate-50 transition-all inline-flex items-center justify-center gap-2"
            >
              Get a Ballpark Estimate
            </Link>
          </div>
          <p className="text-slate-400 text-xs mt-4">No commitment. We usually respond within 1 business day.</p>
        </div>
      </section>

      {/* Capabilities — Grouped by Outcome */}
      <section className="py-24 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
            <span className="w-8 h-[2px] bg-cyan-500"></span>
            What We Can Build for You
          </h2>
          <p className="text-slate-500 text-sm mb-16 max-w-2xl">
            Organised by the business outcome you're after, not the technology behind it.
          </p>
          <div className="space-y-16">
            {capabilityGroups.map((group, gIndex) => (
              <div
                key={gIndex}
                ref={push(capGroupsRef)}
                style={{ opacity: 0 }}
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-10 h-10 bg-cyan-50 flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={group.icon} className="text-cyan-600 text-lg" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">{group.title}</h3>
                </div>
                <p className="text-slate-500 text-sm mb-6 ml-14">{group.description}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-14">
                  {group.items.map((item, iIndex) => (
                    <div
                      key={iIndex}
                      className="bg-white border border-slate-200 p-5 hover:border-cyan-300 transition-all shadow-sm"
                    >
                      <span className="text-sm font-bold text-slate-900">{item.name}</span>
                      <p className="text-xs text-slate-500 mt-1 leading-relaxed">{item.detail}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 bg-slate-50 border-y border-slate-100 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
            <span className="w-8 h-[2px] bg-cyan-500"></span>
            Industries We've Delivered In
          </h2>
          <p className="text-slate-500 text-sm mb-12 max-w-2xl">We work with teams in India and the UK across these sectors.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {industries.map((item, index) => (
              <div
                key={index}
                ref={push(industriesRef)}
                style={{ opacity: 0 }}
                className="p-6 bg-white border border-slate-200 flex flex-col items-center text-center group hover:border-cyan-300 transition-all shadow-sm"
              >
                <SafeIcon icon={item.icon} className="text-2xl text-cyan-600 mb-4 group-hover:scale-110 transition-transform" />
                <span className="text-xs font-bold text-slate-700 uppercase tracking-tighter leading-tight">
                  {item.title}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Teams Choose Us */}
      <section className="py-24 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
            <span className="w-8 h-[2px] bg-cyan-500"></span>
            Why Teams Choose Us
          </h2>
          <p className="text-slate-500 text-sm mb-12 max-w-2xl">What makes us different from larger agencies and freelance marketplaces.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChooseUs.map((item, index) => (
              <div
                key={index}
                ref={push(whyRef)}
                style={{ opacity: 0 }}
                className="bg-white border border-slate-200 p-6 hover:border-cyan-300 transition-all shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <SafeIcon icon={FiCheckCircle} className="text-cyan-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-sm font-bold text-slate-900">{item.title}</span>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">{item.detail}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-20 bg-slate-50 border-y border-slate-100 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
            <span className="w-8 h-[2px] bg-cyan-500"></span>
            How We Work with Your Team
          </h2>
          <p className="text-slate-500 text-sm mb-12 max-w-2xl">A predictable, five-phase process from the first conversation to long-term support.</p>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {processPhases.map((step, index) => (
              <div
                key={index}
                ref={push(processRef)}
                style={{ opacity: 0 }}
                className="bg-white border border-slate-200 p-6 text-center hover:border-cyan-300 transition-all shadow-sm"
              >
                <div className="w-10 h-10 bg-cyan-50 flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={step.icon} className="text-cyan-600 text-lg" />
                </div>
                <div className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-600 mb-2">Phase {index + 1}</div>
                <h3 className="text-sm font-bold text-slate-900 mb-2">{step.phase}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reliability, Security, Support */}
      <section className="py-24 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Reliability & Support */}
            <div
              ref={reliabilityRef}
              style={{ opacity: 0 }}
              className="bg-white border border-slate-200 p-8 shadow-sm"
            >
              <div className="flex items-center gap-3 mb-5">
                <SafeIcon icon={FiShield} className="text-cyan-600 text-xl" />
                <h3 className="text-lg font-bold text-slate-900">Reliability & Support</h3>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed mb-4">
                We design for uptime and monitor all hosted systems. Our infrastructure includes automated backups, health checks, and alerting — so issues get caught before your team notices.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <SafeIcon icon={FiCheckCircle} className="text-cyan-500 mt-0.5 flex-shrink-0 text-xs" />
                  Post-launch bug fixes and enhancements included in support agreements
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <SafeIcon icon={FiCheckCircle} className="text-cyan-500 mt-0.5 flex-shrink-0 text-xs" />
                  Response-time SLAs available for critical systems
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <SafeIcon icon={FiCheckCircle} className="text-cyan-500 mt-0.5 flex-shrink-0 text-xs" />
                  99.9% uptime maintained across hosted client infrastructure over the past 12 months
                </li>
              </ul>
            </div>

            {/* Security & Data Handling */}
            <div
              ref={securityRef}
              style={{ opacity: 0 }}
              className="bg-white border border-slate-200 p-8 shadow-sm"
            >
              <div className="flex items-center gap-3 mb-5">
                <SafeIcon icon={FiLock} className="text-cyan-600 text-xl" />
                <h3 className="text-lg font-bold text-slate-900">Security & Data Handling</h3>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed mb-4">
                Security is built into our development process, not added after the fact. Every system we deliver follows secure coding practices by default.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <SafeIcon icon={FiCheckCircle} className="text-cyan-500 mt-0.5 flex-shrink-0 text-xs" />
                  Encrypted data in transit (TLS) and at rest where applicable
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <SafeIcon icon={FiCheckCircle} className="text-cyan-500 mt-0.5 flex-shrink-0 text-xs" />
                  Least-privilege access controls and role-based permissions
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <SafeIcon icon={FiCheckCircle} className="text-cyan-500 mt-0.5 flex-shrink-0 text-xs" />
                  Secure coding practices aligned with OWASP guidelines
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <SafeIcon icon={FiCheckCircle} className="text-cyan-500 mt-0.5 flex-shrink-0 text-xs" />
                  Audit logging for sensitive operations
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-20 bg-slate-50 border-y border-slate-100 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
            <span className="w-8 h-[2px] bg-cyan-500"></span>
            Core Technology Stack
          </h2>
          <p className="text-slate-500 text-sm mb-10 max-w-2xl">The tools and frameworks we use daily across client projects.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {techStack.map((stack, index) => (
              <div
                key={index}
                ref={push(techRef)}
                style={{ opacity: 0 }}
                className="bg-white border border-slate-200 p-5 shadow-sm"
              >
                <div className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-600 mb-3">{stack.category}</div>
                <p className="text-xs text-slate-600 leading-relaxed">{stack.items}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal Client Profile */}
      <section className="py-16 relative z-10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Who We Work Best With</h2>
          <p className="text-slate-600 text-sm leading-relaxed max-w-2xl mx-auto">
            Our best engagements are with SMEs and mid-market companies — typically 20 to 500 employees — who need a hands-on technical partner, not a vendor. We work especially well with non-technical founders who need someone to translate business problems into working software, and with existing teams who need experienced builders to ship production systems on time. Our primary markets are India and the United Kingdom.
          </p>
        </div>
      </section>

      {/* Final CTA — Hesitant Buyer Friendly */}
      <section className="py-24 relative z-10">
        <div className="max-w-5xl mx-auto px-6">
          <div ref={ctaFinalRef} style={{ opacity: 0 }} className="bg-gradient-to-r from-cyan-600 to-blue-700 p-12 md:p-16 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-20 -mt-20 blur-3xl"></div>
            <div className="relative z-10 text-center">
              <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-6">
                Let's talk about what you need built
              </h2>
              <p className="text-white/80 text-lg mb-4 max-w-2xl mx-auto font-medium">
                Whether you need a full enterprise system, a targeted AI integration, or just an honest assessment of your current setup — we're ready to have that conversation.
              </p>
              <p className="text-white/60 text-sm mb-10 max-w-2xl mx-auto">
                If you're still exploring options, that's fine. We're happy to review your current system or idea and tell you honestly whether we're the right fit — no pressure, no sales pitch.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/book-call"
                  className="px-10 py-4 bg-white text-slate-900 font-bold hover:bg-slate-50 transition-all flex items-center justify-center gap-2 group shadow-xl"
                >
                  Book a 30-Minute Discovery Call
                  <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/contact"
                  className="px-10 py-4 border border-white/30 text-white font-bold hover:bg-white/10 transition-all flex items-center justify-center gap-2"
                >
                  Get a Ballpark Estimate
                </Link>
              </div>
              <p className="text-white/40 text-xs mt-6">
                You'll speak directly with the team that does the work. We respond within 1 business day.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Capabilities;